import { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { ArrowRight, Clock, UserX } from "lucide-react";
import { toast } from "sonner";

export default function RoomLookup() {
  const navigate = useNavigate();
  const { setCurrentRoom, recentRooms, setRecentRooms, clearCart } = useOutletContext();
  const [roomNumber, setRoomNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [guestInfo, setGuestInfo] = useState(null);
  const [lastGuest, setLastGuest] = useState(null);

  const selectRoom = async (room) => {
    const num = room || roomNumber;
    if (!num) return;
    setLoading(true);
    setLastGuest(null);
    try {
      const res = await api.post("/pos/room-lookup", { room_number: num });
      const roomData = { ...res.data, room_number: num };

      if (res.data.guest_name) {
        // In-house guest found
        setCurrentRoom(roomData);
        clearCart();
        setRecentRooms(prev => {
          const filtered = prev.filter(r => r.room_number !== num);
          return [roomData, ...filtered].slice(0, 5);
        });
        setGuestInfo(roomData);
      } else if (res.data.last_guest) {
        // No in-house guest but found last departed guest
        setLastGuest({ ...res.data.last_guest, room_number: num, warning: res.data.warning });
      } else {
        // No guest at all — go straight to menu (manual charge)
        setCurrentRoom(roomData);
        clearCart();
        setRecentRooms(prev => {
          const filtered = prev.filter(r => r.room_number !== num);
          return [roomData, ...filtered].slice(0, 5);
        });
        navigate("/pos/menu");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid room number");
      setRoomNumber("");
    } finally { setLoading(false); }
  };

  const chargeLastGuest = () => {
    if (!lastGuest) return;
    const roomData = {
      room_number: lastGuest.room_number,
      reservation_id: lastGuest.reservation_id,
      guest_name: lastGuest.guest_name,
      confirmation_number: lastGuest.confirmation_number,
      arrival: '',
      departure: lastGuest.departure,
      balance: 0,
    };
    setCurrentRoom(roomData);
    clearCart();
    setRecentRooms(prev => {
      const filtered = prev.filter(r => r.room_number !== lastGuest.room_number);
      return [roomData, ...filtered].slice(0, 5);
    });
    setLastGuest(null);
    setGuestInfo(null);
    navigate("/pos/menu");
  };

  const skipLastGuest = () => {
    const roomData = {
      room_number: lastGuest?.room_number || roomNumber,
      reservation_id: '',
      guest_name: '',
    };
    setCurrentRoom(roomData);
    clearCart();
    setLastGuest(null);
    setGuestInfo(null);
    navigate("/pos/menu");
  };

  const addDigit = (d) => setRoomNumber(prev => (prev + d).slice(0, 10));
  const backspace = () => setRoomNumber(prev => prev.slice(0, -1));
  const clear = () => { setRoomNumber(""); setGuestInfo(null); setLastGuest(null); };

  return (
    <div className="flex flex-col h-full p-4" data-testid="room-lookup">
      <h2 className="font-heading text-lg font-bold tracking-tight mb-4">Enter Room Number</h2>

      {/* Room Display */}
      <div className="flex gap-2 mb-6">
        <div className="flex-1 h-16 flex items-center px-4 border-2 border-black bg-white">
          <span className="font-heading text-3xl font-black tracking-wider" data-testid="room-display">
            {roomNumber || <span className="text-gray-300">Room #</span>}
          </span>
        </div>
      </div>

      {/* Keypad */}
      {!guestInfo && !lastGuest && (
        <div className="grid grid-cols-3 gap-3 max-w-xs mb-6" data-testid="room-keypad">
          {[1,2,3,4,5,6,7,8,9].map(d => (
            <button key={d} onClick={() => addDigit(String(d))}
              className="keypad-btn rounded-none" data-testid={`room-digit-${d}`}>{d}</button>
          ))}
          <button onClick={clear} className="keypad-btn rounded-none text-gray-400 text-sm" data-testid="room-clear">Clear</button>
          <button onClick={() => addDigit("0")} className="keypad-btn rounded-none" data-testid="room-digit-0">0</button>
          <button onClick={backspace} className="keypad-btn rounded-none text-gray-400 text-sm" data-testid="room-backspace">Del</button>
        </div>
      )}

      {/* Guest Card — in-house guest found */}
      {guestInfo && (
        <div className="bg-white border-2 border-black p-4 mb-4 max-w-xs animate-fade-in" data-testid="guest-card">
          <p className="font-heading text-xl font-black tracking-tight">{guestInfo.guest_name}</p>
          <p className="text-xs text-gray-400 uppercase tracking-wider mt-1">Room {guestInfo.room_number}</p>
          {guestInfo.reservation_id && (
            <p className="text-[10px] text-gray-400 mt-1">Res: {guestInfo.reservation_id}</p>
          )}
          <Button onClick={() => { setGuestInfo(null); navigate("/pos/menu"); }}
            className="w-full mt-3 h-11 rounded-none bg-black text-white hover:bg-gray-800 font-bold text-sm"
            data-testid="confirm-guest-button">
            Charge to {guestInfo.guest_name} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}

      {/* Last Guest Card — no in-house guest but found departed guest */}
      {lastGuest && (
        <div className="max-w-xs animate-fade-in space-y-3" data-testid="last-guest-prompt">
          <div className="bg-amber-50 border border-amber-300 p-3">
            <div className="flex items-center gap-2 mb-2">
              <UserX className="w-4 h-4 text-amber-600" />
              <p className="text-xs font-bold uppercase tracking-wider text-amber-700">No current guest</p>
            </div>
            <p className="text-xs text-amber-700">{lastGuest.warning || 'No in-house guest found for this room.'}</p>
          </div>

          <div className="bg-white border-2 border-blue-400 p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-blue-500 mb-1">Last Guest</p>
            <p className="font-heading text-xl font-black tracking-tight">{lastGuest.guest_name}</p>
            <p className="text-xs text-gray-400 mt-1">
              Conf: <span className="font-mono font-medium text-gray-600">{lastGuest.confirmation_number}</span>
            </p>
            <p className="text-xs text-gray-400">Departed: {lastGuest.departure}</p>

            <Button onClick={chargeLastGuest}
              className="w-full mt-3 h-11 rounded-none bg-blue-600 text-white hover:bg-blue-700 font-bold text-sm"
              data-testid="charge-last-guest-button">
              Charge to {lastGuest.guest_name} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button variant="outline" onClick={skipLastGuest}
              className="w-full mt-2 h-9 rounded-none text-xs text-gray-500"
              data-testid="skip-last-guest-button">
              Skip — charge room without guest
            </Button>
          </div>
        </div>
      )}

      {/* Charge Button — when no guest info and no last guest prompt */}
      {roomNumber && !guestInfo && !lastGuest && (
        <Button onClick={() => selectRoom()} disabled={loading}
          className="w-full max-w-xs h-14 rounded-none bg-black text-white hover:bg-gray-800 font-bold text-base animate-fade-in"
          data-testid="select-room-button">
          {loading ? "..." : <>Charge Room {roomNumber} <ArrowRight className="w-5 h-5 ml-2" /></>}
        </Button>
      )}

      {/* Recent Rooms */}
      {recentRooms.length > 0 && !guestInfo && !lastGuest && (
        <div className="mt-auto pt-6" data-testid="recent-rooms">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400 mb-2">
            <Clock className="w-3 h-3 inline mr-1" /> Recent Rooms
          </p>
          <div className="flex gap-2 flex-wrap">
            {recentRooms.map((r) => (
              <button key={r.room_number} onClick={() => { setRoomNumber(r.room_number); selectRoom(r.room_number); }}
                className="px-4 py-3 bg-gray-100 border border-gray-200 hover:bg-gray-200 transition-colors"
                data-testid={`recent-room-${r.room_number}`}>
                <span className="font-heading font-bold text-lg">{r.room_number}</span>
                {r.guest_name && <span className="text-[10px] text-gray-400 block">{r.guest_name}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
